import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert } from "@/components/ui/alert";
import { toast } from "sonner";
import { useState, useContext, useEffect } from "react";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import { ExternalLink } from "lucide-react";

type EmployeeCalComModalProps = {
  isOpen: boolean;
  onClose: () => void;
  employee: {
    id: string;
    name: string;
    calCom?: {
      apiKey?: string;
      username?: string;
      eventTypeId?: string;
      teamId?: string;
    };
  };
};

const EmployeeCalComModal = ({
  isOpen,
  onClose,
  employee,
}: EmployeeCalComModalProps): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  const isRentals = merchantType === "rentals";

  const [apiKey, setApiKey] = useState(employee.calCom?.apiKey || "");
  const [teamId, setTeamId] = useState("");
  const [eventTypeId, setEventTypeId] = useState(employee.calCom?.eventTypeId || "");

  const [isLoadingEventTypes, setIsLoadingEventTypes] = useState(false);
  const [eventTypes, setEventTypes] = useState<
    Array<{ id: number; title: string; slug: string; length: number }>
  >([]);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens or employee changes
  useEffect(() => {
    if (isOpen) {
      setApiKey(employee.calCom?.apiKey || "");
      setTeamId(employee.calCom?.teamId || "");
      setEventTypeId(employee.calCom?.eventTypeId || "");
      setEventTypes([]);
      setError(null);
    }
  }, [isOpen, employee]);

  const { mutate: updateCalCom, isLoading: isSaving } = trpc.employees.updateCalCom.useMutation({
    onSuccess: () => {
      toast.success("Configuración guardada", {
        description: "La configuración de Cal.com se ha guardado exitosamente.",
      });
      utils.employees.list.invalidate({ merchantId });
      onClose();
    },
    onError: (error) => {
      toast.error("Error al guardar", {
        description: error.message || "Ocurrió un error al guardar la configuración.",
      });
    },
  });

  const handleLoadEventTypes = async () => {
    if (isRentals) {
      // For rentals: only API Key is required
      if (!apiKey) {
        setError("Por favor ingresa la API Key");
        return;
      }
    } else {
      // For development: API Key and Team ID are required
      if (!apiKey || !teamId) {
        setError("Por favor ingresa la API Key y el Team ID");
        return;
      }
    }

    setIsLoadingEventTypes(true);
    setError(null);

    try {
      let eventTypesData;
      if (isRentals) {
        eventTypesData = await utils.client.employees.getCalComEventTypesIndividual.query({
          apiKey,
        });
      } else {
        eventTypesData = await utils.client.employees.getCalComEventTypes.query({
          apiKey,
          teamId,
        });
      }
      setEventTypes(eventTypesData);
      if (eventTypesData.length === 0) {
        setError(
          isRentals
            ? "No se encontraron tipos de evento para esta cuenta."
            : "No se encontraron tipos de evento para este equipo."
        );
      }
    } catch (err: any) {
      setError(
        err.message ||
          (isRentals
            ? "Error al cargar los tipos de evento. Verifica tu API Key."
            : "Error al cargar los tipos de evento. Verifica tu API Key y Team ID.")
      );
    } finally {
      setIsLoadingEventTypes(false);
    }
  };

  const handleSave = () => {
    if (isRentals) {
      if (!apiKey || !eventTypeId) {
        toast.error("Campos requeridos", {
          description: "Por favor completa la API Key y selecciona un tipo de evento.",
        });
        return;
      }
    } else {
      if (!apiKey || !teamId || !eventTypeId) {
        toast.error("Campos requeridos", {
          description: "Por favor completa la API Key, Team ID y selecciona un tipo de evento.",
        });
        return;
      }
    }

    updateCalCom({
      merchantId,
      employeeId: employee.id,
      apiKey,
      teamId: isRentals ? undefined : teamId,
      eventTypeId,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Configurar Cal.com - {employee.name}</DialogTitle>
        </DialogHeader>
        <div>
          <div className="flex flex-col gap-4">
            <Alert status="info" className="rounded-md">
              <span className="text-sm">
                Si aún no tienes una cuenta en Cal.com,{" "}
                <a href="https://cal.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-500">
                  regístrate aquí <ExternalLink className="mx-0.5 inline size-3" />
                </a>{" "}
                y configura tu cuenta antes de continuar.
              </span>
            </Alert>

            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium">
                API Key <span className="text-red-500">*</span>
              </Label>
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="cal_live_..."
              />
              <p className="text-xs text-gray-500">
                Puedes encontrar tu API Key en la configuración de tu cuenta de Cal.com
              </p>
            </div>

            {!isRentals && (
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">
                  Team ID <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  placeholder="138103"
                />
                <p className="text-xs text-gray-500">
                  El ID del equipo en Cal.com (puedes encontrarlo en la URL de tu equipo)
                </p>
              </div>
            )}

            <Button
              onClick={handleLoadEventTypes}
              disabled={isLoadingEventTypes || (isRentals ? !apiKey : !apiKey || !teamId)}
            >
              {isLoadingEventTypes ? "Cargando..." : "Cargar Tipos de Evento"}
            </Button>

            {error && (
              <Alert status="error" className="rounded-md">
                <span className="text-sm">{error}</span>
              </Alert>
            )}

            {eventTypes.length > 0 && (
              <>
                <Separator />
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">
                    Tipo de Evento <span className="text-red-500">*</span>
                  </Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    value={eventTypeId}
                    onChange={(e) => setEventTypeId(e.target.value)}
                  >
                    <option value="">Selecciona un tipo de evento</option>
                    {eventTypes.map((eventType) => (
                      <option key={eventType.id} value={eventType.id.toString()}>
                        {eventType.title} - {eventType.length} min
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !apiKey || !eventTypeId}
          >
            {isSaving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeCalComModal;
